import { FunctionComponent } from "react";
import Image from "next/legacy/image";
import { useTranslation } from "next-i18next/pages";
import styles from "../../styles/home/LogoStrip.module.css";

interface Company {
  name: string;
  imgUrl: string;
  companyUrl?: string;
  // For logos that only exist as white assets: invert them to dark.
  invert?: boolean;
}

const COMPANIES: Company[] = [
  {
    name: "Appucations",
    imgUrl: "/images/home/trusted_by/appucations.svg",
    companyUrl: "https://www.appucations.de/"
  },
  {
    name: "MACONIA",
    imgUrl: "/images/home/trusted_by/maconia-transparent.png",
    companyUrl: "https://maconia.de/",
    invert: true
  },
  {
    name: "DGTL MKRS",
    imgUrl: "/images/home/trusted_by/logo-dgtl-makers.svg",
    companyUrl: "https://dgtlmakers.com"
  },
  {
    name: "Prescriva",
    imgUrl: "/images/home/trusted_by/prescriva.png"
  },
  {
    name: "DevGold",
    imgUrl: "/images/home/trusted_by/devgold.png"
  },
  {
    name: "Whatto",
    imgUrl: "/images/home/trusted_by/whatto.svg",
    companyUrl: "https://www.whatto.com/"
  },
  {
    name: "mvpmatch",
    imgUrl: "/images/home/trusted_by/mvpmatch.png",
    companyUrl: "https://mvpmatch.co/"
  },
  {
    name: "MVP Factory",
    imgUrl: "/images/home/trusted_by/mvp_factory.png",
    companyUrl: "https://mvpfactory.co/"
  }
];

interface LogoStripProps {
  variant?: "default" | "contact";
  note?: string;
}

const LogoStrip: FunctionComponent<LogoStripProps> = ({
  variant = "default",
  note
}) => {
  const { t } = useTranslation("common");

  return (
    <section
      className={`${styles.strip} ${variant === "contact" ? styles.contactStrip : ""}`}
      aria-label={t("trustedBy.title")}
    >
      <div className={styles.stripHeading}>
        <p className={styles.label}>{t("trustedBy.title")}</p>
        {note && <span className={styles.note}>{note}</span>}
      </div>

      <div className={styles.marquee}>
        <div className={styles.track}>
          {[0, 1].map((copy: number) => (
            <div
              key={copy}
              className={styles.logos}
              aria-hidden={copy === 1 || undefined}
            >
              {COMPANIES.map((company: Company) => {
                const logoClass = company.invert
                  ? `${styles.logo} ${styles.inverted}`
                  : styles.logo;
                const logo = (
                  <Image
                    src={company.imgUrl}
                    layout="fill"
                    objectFit="contain"
                    objectPosition="center"
                    alt={`${company.name} logo`}
                  />
                );

                return company.companyUrl ? (
                  <a
                    key={company.name}
                    href={company.companyUrl}
                    className={logoClass}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`${company.name} (opens in new tab)`}
                    tabIndex={copy === 1 ? -1 : undefined}
                  >
                    {logo}
                  </a>
                ) : (
                  <div key={company.name} className={styles.logo}>
                    {logo}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default LogoStrip;
